import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { ReportPdfData, pdfColors } from './types';
import { PdfAssets } from '../pdfTemplates';
import { createCoverPage } from './pages/coverPage';
import { createArchitectureOverviewPage } from './pages/architectureOverview';
import { createSourceNodesPage } from './pages/sourceNodes';
import { createArchitectureNodesPage } from './pages/architectureNodes';
import { createDestinationsPages } from './pages/destinations';
import { createBusinessTopicsPages } from './pages/businessTopics';
import { createNextStepsPage } from './pages/nextSteps';

/**
 * Creates a document definition for the MetaRouter report PDF
 * @param data Report data for the PDF
 * @param diagramImageBase64 Base64 encoded image of the diagram
 * @param assets Logo and font assets for the PDF
 * @returns PDF document definition
 */
export const createReportTemplate = (
  data: ReportPdfData,
  diagramImageBase64?: string,
  assets?: PdfAssets
): TDocumentDefinitions => {
  // Create the content array for all pages
  const content: any[] = [];
  
  // Ensure selectedSections is always an array
  const selectedSections = Array.isArray(data.selectedSections) ? data.selectedSections : [];
  
  // Track total page count for page numbering
  let totalPages = 1; // Start with cover page
  
  // Count additional pages based on selected sections
  if (diagramImageBase64) totalPages++; // Architecture Overview
  
  // Count source and architecture pages
  const allSourceTypes = ['mobile-sdk', 'javascript-source', 'server-source'];
  
  // If SOURCES is selected, include all source types
  let sourceTypes = [];
  if (selectedSections.includes('SOURCES')) {
    sourceTypes = [...allSourceTypes];
  } else {
    // Otherwise, filter based on specific selections
    sourceTypes = allSourceTypes.filter(type => 
      selectedSections.includes(`SOURCES:${type}`) || 
      selectedSections.includes(type)
    );
  }
  
  // Define all architecture node types
  const allArchitectureTypes = ['cluster', 'sync-injector', 'load-balancer'];
  
  // If ARCHITECTURE is selected, include all architecture types
  let architectureTypes = [];
  if (selectedSections.includes('ARCHITECTURE')) {
    architectureTypes = [...allArchitectureTypes];
  } else {
    // Otherwise, filter based on specific selections
    architectureTypes = allArchitectureTypes.filter(type => 
      selectedSections.includes(`ARCHITECTURE:${type}`) || 
      selectedSections.includes(type)
    );
  }
  
  let hasSourcePages = false;
  let hasArchitecturePages = false;
  
  if (data.architecture?.nodes) {
    // Check for source nodes
    if (sourceTypes.length > 0 && data.architecture.nodes.some(node => sourceTypes.includes(node.type))) {
      hasSourcePages = true;
      totalPages++;
    }
    
    // Check for architecture nodes
    if (data.architecture.nodes.some(node => architectureTypes.includes(node.type))) {
      hasArchitecturePages = true;
      totalPages++;
    }
  }
  
  // Count destination pages
  let filteredDestinationsCount = 0;
  if (data.destinations && data.destinations.length > 0) {
    // Filter destinations based on selected sections
    const filteredDestinations = data.destinations.filter(dest => {
      // If DESTINATIONS is selected, include all destinations
      if (selectedSections.includes('DESTINATIONS')) {
        return true;
      }
      
      // If it's Facebook Pixel, only include if it's in the selected sections
      if (dest.name === 'Facebook Pixel' || dest.name === 'Meta Pixel') {
        return selectedSections.includes('Facebook Pixel') || 
               selectedSections.includes('DESTINATIONS:Facebook Pixel') ||
               selectedSections.includes('Meta Pixel') ||
               selectedSections.includes('DESTINATIONS:Meta Pixel');
      }
      
      // For other destinations, check if they have nodes and if any of those nodes are in selected sections
      if (dest.nodes && dest.nodes.length > 0) {
        for (const node of dest.nodes) {
          if (node.id && (
              selectedSections.includes(`DESTINATION:${node.id}`) ||
              selectedSections.includes(node.id)
          )) {
            return true;
          }
        }
      }
      
      // Check if the destination itself is selected
      if (selectedSections.includes(dest.name) || 
          selectedSections.includes(`DESTINATIONS:${dest.name}`)) {
        return true;
      }
      
      // By default, don't include the destination if no specific selection is found
      return false;
    });
    
    filteredDestinationsCount = filteredDestinations.length;
    
    // Calculate number of destination pages (2 destinations per page)
    if (filteredDestinationsCount > 0) {
      const destinationPages = Math.ceil(filteredDestinationsCount / 2);
      totalPages += destinationPages;
    }
  }
  
  // Count topic pages
  const businessTopics = [
    'CDP Enhancement',
    'Data Governance & Compliance',
    'Tag Management',
    'Retail Media Networks'
  ];
  
  const selectedBusinessTopics = businessTopics.filter(topic => 
    selectedSections.includes(topic)
  );
  
  totalPages += selectedBusinessTopics.length;
  
  // Add Next Steps page
  totalPages++;

  // 1. Cover Page
  content.push(createCoverPage(data, assets, pdfColors));

  // Track current page number
  let currentPage = 1;

  // 2. Architecture Overview Page (if diagram is provided)
  if (diagramImageBase64) {
    currentPage++;
    content.push(...createArchitectureOverviewPage(
      data, 
      diagramImageBase64, 
      assets, 
      pdfColors, 
      currentPage, 
      totalPages
    ));
  }

  // 3. Architecture Breakdown - Source Nodes (if any)
  if (hasSourcePages) {
    currentPage++;
    content.push(...createSourceNodesPage(
      data,
      assets,
      pdfColors,
      currentPage,
      totalPages
    ));
  }

  // 4. Architecture Breakdown - Architecture Nodes (if any)
  if (hasArchitecturePages) {
    currentPage++;
    content.push(...createArchitectureNodesPage(
      data,
      assets,
      pdfColors,
      currentPage,
      totalPages
    ));
  }

  // 5. Destinations Pages (if any)
  if (filteredDestinationsCount > 0) {
    currentPage++;
    const destinationPages = createDestinationsPages(
      data,
      assets,
      pdfColors,
      currentPage,
      totalPages
    );
    content.push(...destinationPages);
    
    // Update current page based on number of destination pages created
    const destinationPageCount = Math.ceil(filteredDestinationsCount / 2);
    currentPage += destinationPageCount - 1; // -1 because we already incremented once
  }

  // 6. Business Topics Pages
  if (selectedBusinessTopics.length > 0) {
    currentPage++;
    const businessTopicsPages = createBusinessTopicsPages(
      data,
      assets,
      pdfColors,
      currentPage,
      totalPages
    );
    content.push(...businessTopicsPages);
    
    // Update current page based on number of topic pages
    currentPage += selectedBusinessTopics.length - 1; // -1 because we already incremented once
  }

  // 7. Next Steps & Call to Action
  currentPage++;
  content.push(...createNextStepsPage(
    data,
    assets,
    pdfColors,
    currentPage,
    totalPages
  ));

  // Create the document definition with automatic page breaks
  return {
    pageSize: 'A4',
    pageMargins: [0, 0, 0, 0],
    defaultStyle: {
      fontSize: 11,
      lineHeight: 1.5,
      font: 'IBMPlexSans'
    },
    // Enable automatic page breaks for all content
    pageBreakBefore: function(currentNode: any, followingNodesOnPage: any[], _nodesOnNextPage: any[], previousNodesOnPage: any[]) {
      // Check if we're in a destination section
      if (currentNode.id && currentNode.id.toString().includes('destination-') && 
          followingNodesOnPage.length > 0 && 
          previousNodesOnPage.length > 10) {
        // If we're in a destination section and there's not much space left, force a page break
        return true;
      }
      
      // If the current node is a heading and there's not much space left on the page, force a page break
      if (currentNode.style === 'header' || currentNode.style === 'subheader') {
        if (followingNodesOnPage.length < 3 && previousNodesOnPage.length > 5) {
          return true;
        }
      }
      
      // Improved page break for destinations - check content height
      if (currentNode.stack && 
          currentNode.stack.length > 0 && 
          previousNodesOnPage.length > 8 && 
          followingNodesOnPage.length > 3) {
        return true;
      }
      
      // Default behavior - let pdfMake decide
      return false;
    },
    info: {
      title: `${data.title} - MetaRouter Infrastructure Report`,
      author: data.authorEmail,
      subject: 'MetaRouter Infrastructure Report',
      keywords: 'MetaRouter, Infrastructure, Data Flow, Architecture'
    },
    content: content,
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        color: pdfColors.primary,
        margin: [0, 0, 0, 10]
      },
      subheader: {
        fontSize: 14,
        bold: true,
        color: pdfColors.secondary,
        margin: [0, 10, 0, 5]
      },
      tableHeader: {
        bold: true,
        fontSize: 12,
        color: pdfColors.secondary
      },
      error: {
        color: pdfColors.error,
        italics: true
      }
    }
  };
};
